import React, {
  useState, useRef, useEffect, useCallback,
} from 'react';
import ReactDOM from 'react-dom';

import {cx} from '../util';

// Simple ID counter for generating unique tooltip IDs (React 16 doesn't have useId)
let tooltipIdCounter = 0;
function useTooltipId() {
  const idRef = useRef(null);
  if (idRef.current === null) {
    tooltipIdCounter += 1;
    idRef.current = `tooltip-${tooltipIdCounter}`;
  }
  return idRef.current;
}

/**
 * Accessible Tooltip component styled with Tailwind CSS.
 *
 * Usage:
 * ```jsx
 * <Tooltip content="This is a tooltip" placement="top">
 *   <button>Hover me</button>
 * </Tooltip>
 * ```
 *
 * For backwards compatibility with react-bootstrap OverlayTrigger pattern:
 * ```jsx
 * <TooltipTrigger placement="top" overlay={<TooltipContent>Text here</TooltipContent>}>
 *   <button>Hover me</button>
 * </TooltipTrigger>
 * ```
 */

const ARROW_SIZE = 4; // border-4 = 4px
const TOOLTIP_OFFSET = 8; // gap between trigger and tooltip

const arrowStyles = {
  top: 'border-t-gray-900 border-x-transparent border-b-transparent',
  bottom: 'border-b-gray-900 border-x-transparent border-t-transparent',
  left: 'border-l-gray-900 border-y-transparent border-r-transparent',
  right: 'border-r-gray-900 border-y-transparent border-l-transparent',
};

function calculatePosition(triggerRect, tooltipRect, placement) {
  let top;
  let left;
  let arrowTop;
  let arrowLeft;

  switch (placement) {
    case 'bottom':
      top = triggerRect.bottom + TOOLTIP_OFFSET;
      left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
      arrowTop = -ARROW_SIZE * 2;
      arrowLeft = tooltipRect.width / 2 - ARROW_SIZE;
      break;
    case 'left':
      top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
      left = triggerRect.left - tooltipRect.width - TOOLTIP_OFFSET;
      arrowTop = tooltipRect.height / 2 - ARROW_SIZE;
      arrowLeft = tooltipRect.width;
      break;
    case 'right':
      top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
      left = triggerRect.right + TOOLTIP_OFFSET;
      arrowTop = tooltipRect.height / 2 - ARROW_SIZE;
      arrowLeft = -ARROW_SIZE * 2;
      break;
    case 'top':
    default:
      top = triggerRect.top - tooltipRect.height - TOOLTIP_OFFSET;
      left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
      arrowTop = tooltipRect.height;
      arrowLeft = tooltipRect.width / 2 - ARROW_SIZE;
      break;
  }

  return {
    top, left, arrowTop, arrowLeft,
  };
}

/**
 * Main Tooltip component - wraps children with hover tooltip functionality.
 * This is the recommended component to use.
 */
export default function Tooltip({
  children,
  content,
  placement = 'top',
  className,
  style,
  tooltipClassName,
  disabled = false,
}) {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [isPositioned, setIsPositioned] = useState(false);
  const [position, setPosition] = useState({
    top: 0, left: 0, arrowTop: 0, arrowLeft: 0,
  });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const timeoutRef = useRef(null);
  const tooltipId = useTooltipId();

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    setPosition(calculatePosition(triggerRect, tooltipRect, placement));
    setIsPositioned(true);
  }, [placement]);

  const showTooltip = () => {
    if (disabled) return;
    setIsVisible(true);
    setShouldRender(true);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
    setIsPositioned(false);
    // Keep rendered briefly for exit animation
    timeoutRef.current = setTimeout(() => setShouldRender(false), 150);
  };

  // Update position when tooltip becomes visible or on scroll/resize
  useEffect(() => {
    if (!shouldRender) return undefined;

    // Initial position calculation after render
    requestAnimationFrame(updatePosition);

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [shouldRender, updatePosition]);

  useEffect(() => () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  const trigger = (
    <span
      ref={triggerRef}
      className={cx('inline-flex', className)}
      style={style}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
      aria-describedby={isVisible ? tooltipId : undefined}
    >
      {children}
    </span>
  );

  const tooltipElement = shouldRender
    ? ReactDOM.createPortal(
      <span
        ref={tooltipRef}
        id={tooltipId}
        role="tooltip"
        style={{
          position: 'fixed',
          top: position.top,
          left: position.left,
        }}
        className={cx(
          'z-50 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded shadow-lg w-max max-w-xs pointer-events-none',
          'transition-opacity duration-150',
          isVisible && isPositioned ? 'opacity-100' : 'opacity-0',
          tooltipClassName,
        )}
      >
        {content}
        <span
          style={{
            position: 'absolute',
            top: position.arrowTop,
            left: position.arrowLeft,
          }}
          className={cx('w-0 h-0 border-4', arrowStyles[placement] || arrowStyles.top)}
          aria-hidden="true"
        />
      </span>,
      document.body,
    )
    : null;

  return (
    <>
      {trigger}
      {tooltipElement}
    </>
  );
}

/**
 * TooltipContent - For backwards compatibility with react-bootstrap pattern.
 * Used as the `overlay` prop value.
 */
export function TooltipContent({children}) {
  return children;
}

/**
 * TooltipTrigger - Backwards compatible wrapper matching react-bootstrap's OverlayTrigger API.
 *
 * Usage:
 * ```jsx
 * <TooltipTrigger placement="top" overlay={<TooltipContent>Tooltip text</TooltipContent>}>
 *   <button>Hover me</button>
 * </TooltipTrigger>
 * ```
 */
export function TooltipTrigger({
  children,
  overlay,
  placement = 'top',
  ...props
}) {
  // Extract content from overlay (TooltipContent or direct content)
  const content = React.isValidElement(overlay) ? overlay.props.children : overlay;

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Tooltip content={content} placement={placement} {...props}>
      {children}
    </Tooltip>
  );
}

